import React from 'react'
import Row from 'antd/lib/row'
import Col from 'antd/lib/col'
import Flex from 'antd/lib/flex'
import MaterialIcon from '../MaterialIcon'
import { hide } from '../Modal'
import { useTheme } from '../ThemeProvider/ThemeProvider'
import Pricing from '../Pricing'
import usePaywallTracking from '../../../hooks/usePaywallTracking'
import resolvePaywallCopy from '../../../services/paywall-copy'
import { currentRouteFeature } from '../../../services/feature-from-route'
import bg from '../../../assets/images/upgrade_plan_banner.webp'
import { ReactComponent as Frame } from './frame.svg'
import { ReactComponent as CloseIcon } from '../../../assets/images/closeIcon.svg'
import styles from './UpgradePlanModal.module.scss'

const BENEFIT_ICON_STYLE = { fontSize: 32, color: 'rgb(228, 147, 29)' }

// The spec (#1478) swaps the third left-hand bullet -- the tier's generic one,
// "Professional Pitch decks" on Pro -- for the feature the user was just
// blocked from, and leaves the rest of the tier's pitch alone. Held as data
// rather than JSX so that substitution is an array index instead of surgery on
// a rendered tree.
const FEATURE_BULLET_INDEX = 2

const MODAL_DATA_MAPPING = {
  basic: {
    title: 'CREATOR',
    plan: {
      planName: 'basicPlus',
      priceType: 'monthly'
    },
    benefits: [
      { icon: 'article', label: 'Unlimited Scripts' },
      { icon: 'edit', label: 'Write Smarter, Faster with FinalBit Co-Pilot' },
      { icon: 'movie_filter', label: 'Visualize Your Story Like Never Before' },
      { icon: 'savings', label: 'Save 80% on Pre-Production Essentials' }
    ],
    content: (
      <Flex gap={8} vertical>
        <h4>Need More Power?</h4>
      </Flex>
    )
  },
  pro: {
    title: 'PRO',
    plan: {
      planName: 'pro',
      priceType: 'monthly'
    },
    benefits: [
      { icon: 'auto_mode', label: 'Cut Your Prep Time by 90%' },
      { icon: 'savings', label: 'Save Up to 80% on Costs' },
      { icon: 'movie_filter', label: 'Professional Pitch decks' }
    ],
    content: (
      <Flex gap={24} vertical>
        <h4>Go Pro Today! Elevate your process</h4>
      </Flex>
    )
  },
  dashboard: (props) => {
    return {
      title: 'CREATOR',
      plan: {
        planName: 'basicPlus',
        priceType: 'monthly'
      },
      benefits: null,
      content: (
        <Flex gap={8} vertical>
          <h4>Need More Power?</h4>
        </Flex>
      )
    }
  }

}

const withFeatureBullet = (benefits, bullet) => {
  if (!benefits || !bullet || benefits.length <= FEATURE_BULLET_INDEX) return benefits
  return benefits.map((benefit, index) => (
    index === FEATURE_BULLET_INDEX ? { ...benefit, label: bullet } : benefit
  ))
}

const Benefits = ({ benefits }) => (
  <div className={styles.benefits}>
    {benefits.map(({ icon, label }) => (
      <div className={styles.benefit} key={label}>
        <MaterialIcon variant='selected' name={icon} style={BENEFIT_ICON_STYLE} />
        <span>
          <strong>{label}</strong>
        </span>
      </div>
    ))}
  </div>
)

// h2 34/700 over a 14/300 paragraph, per the spec. The tag is the bolded
// lead-in ("Instant Line-Item Budgets.") and is often absent -- the shorter
// limit copy is one plain sentence.
const PaywallCopy = ({ copy }) => (
  <div className={styles.copyBlock}>
    <h2 className={styles.headline}>{copy.headline}</h2>
    {(copy.subtextTag || copy.subtext) && (
      <p className={styles.subtext}>
        {copy.subtextTag && <strong>{copy.subtextTag} </strong>}
        {copy.subtext}
      </p>
    )}
  </div>
)

const CloseButton = ({ onClose, onTrack }) => {
  return (
    <button
      type='button'
      aria-label='Close Modal'
      aria-labelledby='close-modal'
      className={styles.modalClose}
      onClick={() => {
        if (onTrack) onTrack('close_x')
        hide('upgradeYourPlan')
        if (onClose) {
          onClose()
        }
      }}
      id='closeModalId'
    >
      <div className={styles.iconContainer}>
        <CloseIcon />
      </div>

    </button>
  )
}

const UpgradePlanBody = ({
  type,
  closable = true,
  containerStyle,
  surface = 'upgrade_modal',
  paywallType = 'feature',
  featureName,
  trigger,
  agent,
  model,
  targetPath,
  vars,
  ...props
}) => {
  const { themeMode } = useTheme() || {}
  // The modal and the full-page limit screen share this component, so the
  // surface cannot be inferred from inside it -- LimitExceeded passes it in.
  // Inferring from `closable` would work today and break silently the first
  // time a non-closable modal appears.
  const { trackCta } = usePaywallTracking({
    surface,
    paywallType,
    featureName,
    impressionEvent: 'paywall_impression',
    ctaEvent: 'paywall_cta_click',
    // `trigger` names the control that opened this paywall -- feature_name says
    // *what* was gated, trigger says *which button* gated it, and the same
    // feature is reachable from several (Tools menu, a hub card, a route
    // guard). It rides in free-form props rather than a column: see
    // show-paywall.js for the vocabulary and the trade that choice makes.
    // `undefined` drops out at JSON.stringify, so unthreaded call sites simply
    // omit the key rather than writing a null. `targetPath` is already
    // uuid-stripped by show-paywall.js before it gets here -- see that
    // file's vocabulary comment for what target_path means next to path.
    props: { required_plan: type, trigger, agent, model, target_path: targetPath }
  })

  const dataType = MODAL_DATA_MAPPING[type === 'pro' ? 'pro' : type === 'dashboard' ? 'dashboard' : 'basic']
  const handleOnClick = () => {
    hide('upgradeYourPlan')
    props?.handleOnClick()
  }
  const data = typeof dataType === 'function' ? dataType({ handleOnClick, ...props }) : dataType
  // Same feature the impression is recorded under: an explicit featureName
  // when the call site declared one, otherwise the route -- see
  // paywall-analytics.js, which falls back the same way. Copy and analytics
  // disagreeing about what was gated would make the funnel unreadable.
  const copy = resolvePaywallCopy({
    featureName: featureName || currentRouteFeature(),
    trigger,
    vars
  })
  // Null when the matrix has no entry for this gate, which is a supported
  // case: the tier's own headline and bullets show instead.
  const benefits = withFeatureBullet(data.benefits, copy?.bullet)
  const hasIntroContent = !!benefits
  return (
    <div className={`${styles.container} ${styles[themeMode]}`} style={containerStyle}>
      <Row align='top' style={{ width: '100%' }}>
        {hasIntroContent && (
          <Col lg={9} sm={0} xs={24} md={9} className={styles.left}>
            <img src={bg} alt='upgrade plan bg' />
            <div className={styles.textBlock}>
              <div className={styles.gem}>
                <div className={styles.bg}>
                  <Frame />
                </div>
                <div className={styles.iconInner}>
                  <MaterialIcon
                    outlined variant='label-default'
                    name='diamond'
                    style={{ fontSize: 35, color: 'rgba(228, 147, 29, 1)' }}
                  />
                </div>
              </div>
              <span className={styles.title}>Upgrade to</span>
              <span className={styles.title}>
                <strong>FinalBit {data.title}</strong>
              </span>
            </div>
            <div className={styles.introBlock} style={{ marginTop: 10 }}>
              <Benefits benefits={benefits} />
            </div>
            <div className={styles.learnMore}>
              <a
                href='https://www.nolanai.app/pricing' target='_blank' rel='noreferrer'
                onClick={() => trackCta('learn_more')}
              >Click to learn more about FinalBit
                subscriptions
              </a>
            </div>
          </Col>
        )}
        <Col lg={hasIntroContent ? 15 : 24} sm={24} xs={24} md={hasIntroContent ? 15 : 24} className={styles.right}>
          {closable && <CloseButton onClose={props?.handleOnClose} onTrack={trackCta} />}
          <div className={styles.mainInfoWrapper}>
            {copy ? <PaywallCopy copy={copy} /> : data.content}
            <Pricing
              // `type` is the tier the blocked action requires -- the first
              // argument every showPaywall call site passes. Pricing turns it
              // into the recommended (highlighted, leading) card, so the plan
              // on offer matches the gate the user just hit instead of always
              // being Creator.
              requiredPlan={type}
              withFreeOption={props.withFreeOption}
              onFreePlanClicked={handleOnClick}
              onPlanCtaTracked={trackCta}
            />
          </div>
        </Col>
      </Row>
    </div>
  )
}

export default UpgradePlanBody
